import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Calendar, Plus, Calculator, CheckCircle, AlertCircle } from 'lucide-react';
import { usePayrollPeriods } from '@/hooks/usePayrollPeriods';
import { usePayrollCalculations } from '@/hooks/usePayrollCalculations';
import { format } from 'date-fns';

export function PayrollPeriodManager() {
  const { periods, isLoading, createPeriod, isCreating, getMonthName, getStatusBadgeVariant } = usePayrollPeriods();
  const [selectedPeriod, setSelectedPeriod] = useState<string>('');
  const [newPeriod, setNewPeriod] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
  });

  const { calculateBulkPayroll, isCalculating, approvePayroll, isApproving } = usePayrollCalculations(selectedPeriod);

  const handleCreatePeriod = () => {
    createPeriod(newPeriod);
  };

  const handleBulkCalculation = () => {
    if (selectedPeriod) {
      calculateBulkPayroll(selectedPeriod);
    }
  };

  const handleApprovePeriod = () => {
    if (selectedPeriod) {
      // In a real app, you'd get the current user ID
      approvePayroll({ periodId: selectedPeriod, approvedBy: 'current-user' });
    }
  };

  const selectedPeriodData = periods?.find(p => p.id === selectedPeriod);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="text-center text-muted-foreground">Loading payroll periods...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Create New Period */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Create New Payroll Period
          </CardTitle>
          <CardDescription>Create a new monthly payroll period for processing</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="year">Year</Label>
              <Input
                id="year"
                type="number"
                min="2020"
                max="2030"
                value={newPeriod.year}
                onChange={(e) => setNewPeriod(prev => ({ ...prev, year: parseInt(e.target.value) }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="month">Month</Label>
              <Select 
                value={newPeriod.month.toString()} 
                onValueChange={(value) => setNewPeriod(prev => ({ ...prev, month: parseInt(value) }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select month" />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 12 }, (_, i) => (
                    <SelectItem key={i + 1} value={(i + 1).toString()}>
                      {getMonthName(i + 1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button onClick={handleCreatePeriod} disabled={isCreating} className="w-full">
            {isCreating ? 'Creating...' : 'Create Period'}
          </Button>
        </CardContent>
      </Card>

      {/* Existing Periods */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Payroll Periods
          </CardTitle>
          <CardDescription>Manage existing payroll periods</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {periods && periods.length > 0 ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="periodSelect">Select Period</Label>
                <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a payroll period" />
                  </SelectTrigger>
                  <SelectContent>
                    {periods.map((period) => (
                      <SelectItem key={period.id} value={period.id}>
                        {getMonthName(period.period_month)} {period.period_year} - 
                        <Badge variant={getStatusBadgeVariant(period.status)} className="ml-2">
                          {period.status.charAt(0).toUpperCase() + period.status.slice(1)}
                        </Badge>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedPeriodData && (
                <>
                  <Separator />
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm text-muted-foreground">Period</Label>
                        <div className="font-medium">
                          {getMonthName(selectedPeriodData.period_month)} {selectedPeriodData.period_year}
                        </div>
                      </div>
                      <div>
                        <Label className="text-sm text-muted-foreground">Status</Label>
                        <div>
                          <Badge variant={getStatusBadgeVariant(selectedPeriodData.status)}>
                            {selectedPeriodData.status.charAt(0).toUpperCase() + selectedPeriodData.status.slice(1)}
                          </Badge>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm text-muted-foreground">Start Date</Label>
                        <div className="font-medium">
                          {format(new Date(selectedPeriodData.start_date), 'MMM dd, yyyy')}
                        </div>
                      </div>
                      <div>
                        <Label className="text-sm text-muted-foreground">End Date</Label>
                        <div className="font-medium">
                          {format(new Date(selectedPeriodData.end_date), 'MMM dd, yyyy')}
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      {selectedPeriodData.status === 'draft' && (
                        <Button
                          onClick={handleBulkCalculation}
                          disabled={isCalculating}
                          className="flex items-center gap-2"
                        >
                          <Calculator className="h-4 w-4" />
                          {isCalculating ? 'Calculating...' : 'Calculate All Payrolls'}
                        </Button>
                      )}
                      
                      {selectedPeriodData.status === 'calculated' && (
                        <Button
                          onClick={handleApprovePeriod}
                          disabled={isApproving}
                          variant="default"
                          className="flex items-center gap-2"
                        >
                          <CheckCircle className="h-4 w-4" />
                          {isApproving ? 'Approving...' : 'Approve Period'}
                        </Button>
                      )}
                      
                      {selectedPeriodData.status === 'approved' && (
                        <div className="flex items-center gap-2 text-green-600">
                          <CheckCircle className="h-4 w-4" />
                          <span className="text-sm font-medium">Period Approved</span>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <AlertCircle className="h-8 w-8 mx-auto mb-2" />
              <p>No payroll periods found. Create your first period above.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
